import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

// Update this if your technician api.js already exports a BASE_URL constant.
const BASE_URL = "https://cbackend.xraidigital.com";

// Same palette used in OnsiteDashboard so the page feels consistent
const COLORS = {
  white: "#ffffff",
  success: "#16a34a",
  danger: "#dc2626",
  lightGrey: "#f8f9fa",
  mediumGrey: "#e1e5e9",
  darkGrey: "#6c757d",
  lightText: "#8e9aaf",
  mediumText: "#6b7280",
  darkText: "#1f2937",
};

function AddPatient() {
  const location = useLocation();
  const navigate = useNavigate();

  const { technicianId, campId, packageId, packageName } = location.state || {};

  // Mirrors OnsiteDashboard's patientForm shape
  const [patientForm, setPatientForm] = useState({
    patient_id: "",
    name: "",
    age: "",
    gender: "",
    phone: "",
    services: [],
    load_number: "" // "Other Number" field, kept same key as onsite for backend compatibility
  });

  const [availableServices, setAvailableServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [servicesError, setServicesError] = useState(null);

  const [isSubmittingPatient, setIsSubmittingPatient] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (!technicianId || !campId || !packageId) {
      setServicesError("Missing technician, camp, or package information.");
      setLoadingServices(false);
      return;
    }
    fetchAvailableServices();
  }, [technicianId, campId, packageId]);

  const fetchAvailableServices = async () => {
    setLoadingServices(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/services/`);
      const serviceList = Array.isArray(response.data)
        ? response.data
        : response.data.services || [];
      setAvailableServices(serviceList);
    } catch (error) {
      console.error("Error fetching services:", error);
      setServicesError("Failed to load services.");
      setAvailableServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const handlePatientFormChange = (field, value) => {
    setPatientForm((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceToggle = (serviceName) => {
    setPatientForm((prev) => {
      const currentServices = prev.services || [];
      const isSelected = currentServices.includes(serviceName);

      return {
        ...prev,
        services: isSelected
          ? currentServices.filter((s) => s !== serviceName)
          : [...currentServices, serviceName]
      };
    });
  };

  // Same validation shape as onsiteAPI.validatePatientData
  const validatePatientData = (form) => {
    const errors = [];
    if (!form.patient_id || !form.patient_id.toString().trim()) errors.push("Patient ID");
    if (!form.name || !form.name.trim()) errors.push("Patient Name");
    if (!form.age || isNaN(form.age) || Number(form.age) <= 0) errors.push("Age");
    if (!form.gender) errors.push("Gender");
    if (!form.services || form.services.length === 0) errors.push("Services");
    return { isValid: errors.length === 0, errors };
  };

  const handleSubmitPatient = async (e) => {
    e.preventDefault();
    setSaveError(null);
    setSuccessMessage(null);

    const validation = validatePatientData(patientForm);
    if (!validation.isValid) {
      setSaveError(`Please fill in: ${validation.errors.join(", ")}`);
      return;
    }

    setIsSubmittingPatient(true);
    try {
      const payload = {
        patient_id: patientForm.patient_id.trim(),
        name: patientForm.name.trim(),
        age: Number(patientForm.age),
        gender: patientForm.gender,
        phone: patientForm.phone.trim() || null,
        other_number: patientForm.load_number.trim() || null,
        services: patientForm.services, // service names, matching onsite payload shape
        camp_id: campId,
        package_id: packageId,
        registered_by: technicianId
      };

      await axios.post(`${BASE_URL}/api/campmanager/patients/`, payload);

      setSuccessMessage("Patient added successfully!");

      // Give the user a moment to see the confirmation, then return to the
      // dashboard. Navigating back remounts it, refreshing the patient list.
      setTimeout(() => {
        navigate(-1);
      }, 800);
    } catch (error) {
      console.error("Error adding patient:", error);
      setSaveError(
        error.response?.data?.message || "Failed to add patient. Please try again."
      );
    } finally {
      setIsSubmittingPatient(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: COLORS.lightGrey, padding: "32px 16px" }}>
      <div style={{ maxWidth: "560px", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px"
          }}
        >
          <h1 style={{ fontSize: "22px", fontWeight: "700", color: COLORS.darkText, margin: 0 }}>
            Add Patient - {packageName || "Package"} (Camp #{campId})
          </h1>
          <button
            onClick={() => navigate(-1)}
            style={{
              backgroundColor: COLORS.darkGrey,
              color: COLORS.white,
              border: "none",
              borderRadius: "8px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer"
            }}
          >
            ← Back
          </button>
        </div>

        <div
          style={{
            backgroundColor: COLORS.white,
            borderRadius: "12px",
            padding: "32px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            border: `1px solid ${COLORS.mediumGrey}`
          }}
        >
          {successMessage && (
            <div
              style={{
                backgroundColor: "#dcfce7",
                border: "1px solid #86efac",
                color: "#166534",
                padding: "12px 16px",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "14px"
              }}
            >
              {successMessage}
            </div>
          )}
          {saveError && (
            <div
              style={{
                backgroundColor: "#fee2e2",
                border: "1px solid #fca5a5",
                color: "#991b1b",
                padding: "12px 16px",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "14px"
              }}
            >
              {saveError}
            </div>
          )}

          <form onSubmit={handleSubmitPatient}>
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Patient ID *</label>
              <input
                type="text"
                value={patientForm.patient_id}
                onChange={(e) => handlePatientFormChange("patient_id", e.target.value)}
                placeholder="Enter patient ID"
                required
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Patient Name *</label>
              <input
                type="text"
                value={patientForm.name}
                onChange={(e) => handlePatientFormChange("name", e.target.value)}
                placeholder="Enter patient's full name"
                required
                style={inputStyle}
              />
            </div>

            <div style={{ display: "flex", gap: "16px", marginBottom: "20px" }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Age *</label>
                <input
                  type="number"
                  value={patientForm.age}
                  onChange={(e) => handlePatientFormChange("age", e.target.value)}
                  placeholder="Enter age"
                  required
                  min="1"
                  max="120"
                  style={inputStyle}
                />
              </div>

              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Gender *</label>
                <select
                  value={patientForm.gender}
                  onChange={(e) => handlePatientFormChange("gender", e.target.value)}
                  required
                  style={{ ...inputStyle, backgroundColor: COLORS.white, cursor: "pointer" }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Phone Number</label>
              <input
                type="tel"
                value={patientForm.phone}
                onChange={(e) => handlePatientFormChange("phone", e.target.value)}
                placeholder="Enter phone number (e.g., +91 9876543210)"
                pattern="[+]?[0-9\s\-]{10,15}"
                style={inputStyle}
              />
              <p style={hintStyle}>Include country code if applicable</p>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Other Number</label>
              <input
                type="text"
                value={patientForm.load_number}
                onChange={(e) => handlePatientFormChange("load_number", e.target.value)}
                placeholder="Enter alternate/other number"
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={labelStyle}>Services *</label>

              {loadingServices ? (
                <div style={{ padding: "12px", textAlign: "center", color: COLORS.mediumText }}>
                  Loading services...
                </div>
              ) : servicesError ? (
                <p style={{ color: COLORS.danger, fontSize: "14px" }}>{servicesError}</p>
              ) : (
                <div
                  style={{
                    border: `1px solid ${COLORS.mediumGrey}`,
                    borderRadius: "8px",
                    padding: "12px",
                    maxHeight: "220px",
                    overflowY: "auto",
                    backgroundColor: COLORS.white
                  }}
                >
                  {availableServices.length === 0 ? (
                    <p style={{ color: COLORS.mediumText, margin: 0, padding: "8px" }}>
                      No services available
                    </p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                      {availableServices.map((service) => {
                        const serviceName = service.name ?? service.service_name;
                        const isChecked = patientForm.services.includes(serviceName);
                        return (
                          <label
                            key={service.id ?? serviceName}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              padding: "8px",
                              cursor: "pointer",
                              borderRadius: "4px",
                              backgroundColor: isChecked ? COLORS.lightGrey : "transparent"
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleServiceToggle(serviceName)}
                              style={{ marginRight: "8px", width: "16px", height: "16px", cursor: "pointer" }}
                            />
                            <span
                              style={{
                                fontSize: "14px",
                                color: COLORS.darkText,
                                fontWeight: isChecked ? "500" : "400"
                              }}
                            >
                              {serviceName}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {patientForm.services.length > 0 && (
                <p style={hintStyle}>Selected: {patientForm.services.join(", ")}</p>
              )}
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                borderTop: `1px solid ${COLORS.mediumGrey}`,
                paddingTop: "20px"
              }}
            >
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isSubmittingPatient}
                style={{
                  padding: "10px 20px",
                  border: `1px solid ${COLORS.mediumGrey}`,
                  borderRadius: "8px",
                  backgroundColor: COLORS.white,
                  color: COLORS.mediumText,
                  cursor: isSubmittingPatient ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500"
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmittingPatient}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor: isSubmittingPatient ? COLORS.mediumGrey : COLORS.success,
                  color: COLORS.white,
                  cursor: isSubmittingPatient ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                {isSubmittingPatient ? (
                  <>
                    <span
                      style={{
                        width: "16px",
                        height: "16px",
                        border: "2px solid transparent",
                        borderTop: "2px solid white",
                        borderRadius: "50%",
                        display: "inline-block",
                        animation: "spin 1s linear infinite"
                      }}
                    />
                    Adding...
                  </>
                ) : (
                  "Add Patient"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Spinner keyframes, since inline styles can't define @keyframes */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

const labelStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "500",
  color: COLORS.darkText,
  marginBottom: "8px"
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  border: `1px solid ${COLORS.mediumGrey}`,
  borderRadius: "8px",
  fontSize: "14px",
  outline: "none",
  boxSizing: "border-box"
};

const hintStyle = {
  fontSize: "12px",
  color: COLORS.lightText,
  marginTop: "8px",
  margin: "8px 0 0 0"
};

export default AddPatient;